import type { ZodType } from 'zod'

import type { AnyValue } from '@mntn-dev/utility-types'

import type { Err } from './err.ts'
import type { Ok } from './ok.ts'

export type Maybe<T, E> = Ok<T> | Err<E>

/**
 * This is a type guard that checks if the Maybe object is an Err object.
 * @param maybe the Maybe object to be inspected
 * @returns boolean indicating if the Maybe object is an Err object
 */
export const isErr = <T, E>(maybe: Maybe<T, E>): maybe is Err<E> =>
  maybe.error !== null && maybe.error !== undefined

/**
 * This is a type guard that checks if the Maybe object is an Ok object.
 * @param maybe the Maybe object to be inspected
 * @returns boolean indicating if the Maybe object is an Ok object
 */
export const isOk = <T, E>(maybe: Maybe<T, E>): maybe is Ok<T> =>
  !isErr(maybe) && 'data' in maybe

/**
 * This function is a type guard that checks if the Maybe object is an Ok object.
 * If the Maybe object is an Ok object, it returns the data contained in the Ok object.
 * If the Maybe object is an Err object, it throws an error.
 * @param maybe the Maybe object to be inspected
 * @returns the data contained in the Ok object
 * @throws if the Maybe object is an Err object
 */
export const assertOk = <T, E>(maybe: Maybe<T, E>): T => {
  if (isErr(maybe)) {
    throw maybe.error
  }
  return maybe.data
}

/**
 * Asserts that a given input is a Maybe type with validated T or E using Zod schemas.
 * Throws an error if validation fails.
 * @param input the input to be validated
 * @param dataSchema the Zod schema for the data part of Ok
 * @param errorSchema the Zod schema for the error part of Err
 */
export function assertMaybe<T, E>(
  input: AnyValue,
  dataSchema: ZodType<T, AnyValue>,
  errorSchema: ZodType<E, AnyValue>
): Maybe<T, E> {
  if (isOk<T, E>(input as Maybe<T, E>)) {
    const result = dataSchema.safeParse((input as Ok<T>).data)
    if (!result.success) {
      throw new Error('Data validation failed', { cause: result.error })
    }
    return input
  }

  if (isErr<T, E>(input as Maybe<T, E>)) {
    const result = errorSchema.safeParse((input as Err<E>).error)
    if (!result.success) {
      throw new Error('Error validation failed', { cause: result.error })
    }
    return input
  }

  throw new Error('Input is not a valid Maybe type')
}
